import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { useState } from 'react';
import { Box } from '@/components/Box/Box';
import { IconButton } from '@/components/IconButton/IconButton';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { useFormik } from 'formik';
import EditIcon from '@/images/EditIcon.svg?react';
import SaveIcon from '@/images/SaveIcon.svg?react';
import CloseIcon from '@/images/CloseIconNew.svg?react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CheckedIcon from '@/images/CheckSmallIcon.svg?react';
import DashedIcon from '@/images/DashIcon.svg?react';
import { EDIT_ORGANIZATION_DETAILS_VALIDATION_SCHEMA } from '@/bundle/_MasterAdmin/ui/const';
import { PrimaryDetailsPanel } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanel';
import { PrimaryDetailsPanelHeader } from '@/bundle/shared/components/PrimaryDetailsPanel/styles';
import { PrimaryDetailsPanelColumn } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanelColumn';
import { EditInternalOrganizationDetailsPayloadType } from '@/api/v1/organization/internal/editInternalOrganizationDetailsApi';
import { editInternalOrganizationDetails } from '@/bundle/_MasterAdmin/api/api';
import { Loader } from '@/components/Loader/Loader';
import { OrganizationStatus } from '@/bundle/_Opco/ui/OrganizationDetails/ui/OrganizationStatus';
import { getChangedFormValues } from '@/helpers/formHelpers/formHelpers';
import { InternalOrganizationType } from '../../types/types';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { useUser } from '@/context/userContext';
import { getUsersLimit, getUsersMaxLimit } from '@/helpers/billingHelpers';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';

type OrganizationDetailsPanelType = {
  organizationDetails?: InternalOrganizationType;
};

export const OrganizationDetailsPanel = ({ organizationDetails }: OrganizationDetailsPanelType) => {
  const queryClient = useQueryClient();
  const { user, refetchUser } = useUser();
  const { isCancelledSubscription } = useSubscriptionStatus();

  const [isEdit, setIsEdit] = useState(false);

  const closeEdit = () => setIsEdit(false);

  const startEdit = () => setIsEdit(true);

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['edit_organization_details'],
    mutationFn: (values: EditInternalOrganizationDetailsPayloadType) => {
      return editInternalOrganizationDetails(values);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      queryClient.setQueryData(['get_internal_organization_details'], response);
      refetchUser();
      setIsEdit(false);
    },
  });

  const formik = useFormik({
    initialValues: {
      name: organizationDetails?.name,
    },
    enableReinitialize: true,
    validationSchema: EDIT_ORGANIZATION_DETAILS_VALIDATION_SCHEMA,
    onSubmit: (values: EditInternalOrganizationDetailsPayloadType) => {
      const payload = getChangedFormValues(values, formik.initialValues);

      if (!payload) {
        closeEdit();

        return;
      }

      return mutate(values);
    },
  });

  const formattedDateCreated = formatDate(organizationDetails?.date_created);
  const maxUsersLimit = getUsersMaxLimit(user);
  const usersLimit = getUsersLimit(organizationDetails, maxUsersLimit);

  return (
    <FormikForm value={formik}>
      <PrimaryDetailsPanel
        headerSlot={
          <>
            {isEdit ? (
              <FormikInput autoFocus name='name' disabled={!isEdit} variant='small' apiError={data?.error} />
            ) : (
              <PrimaryDetailsPanelHeader>{organizationDetails?.name}</PrimaryDetailsPanelHeader>
            )}

            {!isCancelledSubscription && (
              <>
                {isEdit && !isPending && (
                  <Box display='flex' columnGap='12px'>
                    <IconButton type='submit'>
                      <SaveIcon />
                    </IconButton>

                    <IconButton onClick={closeEdit} svgPath='stroke'>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
                {isPending && <Loader width={40} backgroundColor='transparent' />}
                {!isEdit && (
                  <IconButton onClick={startEdit}>
                    <EditIcon />
                  </IconButton>
                )}
              </>
            )}
          </>
        }
      >
        <PrimaryDetailsPanelColumn name='Status'>
          <OrganizationStatus status={organizationDetails?.status} />
        </PrimaryDetailsPanelColumn>
        <PrimaryDetailsPanelColumn name='Creation Date'>{formattedDateCreated}</PrimaryDetailsPanelColumn>
        <PrimaryDetailsPanelColumn name='Users Limit'>{usersLimit}</PrimaryDetailsPanelColumn>
        <PrimaryDetailsPanelColumn name='ID Verification Enabled'>
          {organizationDetails?.is_kyc_allowed ? <CheckedIcon /> : <DashedIcon />}
        </PrimaryDetailsPanelColumn>
      </PrimaryDetailsPanel>
    </FormikForm>
  );
};
